import React, { useState, useEffect } from 'react';
import { Plus, Cloud, CloudOff, LogOut, Home, Globe, Moon, Sun } from 'lucide-react';

import useAuth from './hooks/useAuth';
import useFirebaseData from './hooks/useFirebaseData';
import { useAnimeFilters } from './hooks/useAnimeFilters';
import { createNewAnime, getAnimeStatus, getAnimeRank } from './utils/animeUtils';
import useSuggestions from './hooks/useSuggestions';
import { LanguageProvider, useLanguage } from './hooks/useLanguage';
import { DarkModeProvider, useDarkMode } from './hooks/useDarkMode';
import AuthScreen from './components/Auth/AuthScreen';
import AnimeCard from './components/Anime/AnimeCard';
import AnimeEditForm from './components/Anime/AnimeEditForm';
import AnimeDetailView from './components/Anime/AnimeDetailView';
import StatsCard from './components/Anime/StatsCard';
import RatingTrendChart from './components/Anime/RatingTrendChart';
import AddAnimeModal from './components/Modal/AddAnimeModal';
import EpisodeRatingModal from './components/Modal/EpisodeRatingModal';
import SearchAndFilters from './components/Search/SearchAndFilters';

// メインアプリコンポーネント
const AnimeRatingApp = () => {
  const { user, loading: authLoading, signUp, signIn, signOut, resetPassword } = useAuth();
  const { t, toggleLanguage, language } = useLanguage();
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const { animes, saveData, uploadImage, loading: dataLoading, lastSync } = useFirebaseData(user);

  // フィルタリング用のカスタムフック
  const filters = useAnimeFilters(animes, t);

  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedAnime, setSelectedAnime] = useState(null);
  const [ratingEpisode, setRatingEpisode] = useState(null);
  const [editingAnime, setEditingAnime] = useState(null);

  // サジェスト用のデータ
  const suggestions = {
    studio: useSuggestions(animes, 'studio'),
    director: useSuggestions(animes, 'director'),
    streamingSite: useSuggestions(animes, 'streamingSite'),
    opArtist: useSuggestions(animes, 'opArtist'),
    edArtist: useSuggestions(animes, 'edArtist')
  };

  // 認証ローディング中
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-lg">{t('loading')}</p>
        </div>
      </div>
    );
  }

  // 未認証の場合は認証画面を表示
  if (!user) {
    return (
      <AuthScreen
        onSignIn={signIn}
        onSignUp={signUp}
        onReset={resetPassword}   /* ★ 追加 */
      />
    );
  }

  // 新しいアニメを追加
  const addAnime = async (formData) => {
    const anime = createNewAnime(formData);
    await saveData([...animes, anime]);
    setShowAddForm(false);
  };

  // アニメを削除
  const deleteAnime = async (animeId) => {
    await saveData(animes.filter(anime => anime.id !== animeId));
  };

  // アニメを更新
  const updateAnime = async (updatedAnime) => {
    const newAnimes = animes.map(anime =>
      anime.id === updatedAnime.id ? updatedAnime : anime
    );
    await saveData(newAnimes);
    setEditingAnime(null);

    // 選択されたアニメも更新
    if (selectedAnime?.id === updatedAnime.id) {
      setSelectedAnime(updatedAnime);
    }
  };

  // エピソードの評価を更新
  const updateEpisodeRating = async (animeId, episodeId, rating, notes = '', detailedRating = null, storyTitle = '') => {
    const newAnimes = animes.map(anime => {
      if (anime.id === animeId) {
        return {
          ...anime,
          episodes: anime.episodes.map(episode => {
            if (episode.id === episodeId) {
              const hasRating = anime.ratingMode === 'detailed' 
                ? (detailedRating && (detailedRating.content > 0 || detailedRating.animation > 0 || detailedRating.sound > 0))
                : rating > 0;
                
              return {
                ...episode,
                rating,
                detailedRating: detailedRating || episode.detailedRating,
                notes,
                storyTitle,
                watchedDate: hasRating ? new Date().toISOString() : null
              };
            }
            return episode;
          })
        };
      }
      return anime;
    });

    await saveData(newAnimes);

    // 選択されたアニメの情報を更新
    const updatedAnime = newAnimes.find(a => a.id === animeId);
    if (updatedAnime && selectedAnime?.id === animeId) {
      setSelectedAnime(updatedAnime);
    }
  };

  // アニメを途中で脱落
  const dropAnime = async (animeId, episodeNumber) => {
    const newAnimes = animes.map(anime => {
      if (anime.id === animeId) {
        return {
          ...anime,
          dropped: true,
          droppedAtEpisode: episodeNumber
        };
      }
      return anime;
    });

    await saveData(newAnimes);
    
    if (selectedAnime?.id === animeId) {
      setSelectedAnime({ ...selectedAnime, dropped: true, droppedAtEpisode: episodeNumber });
    }
  };

  const reviveAnime = async (animeId) => {
    const newAnimes = animes.map(anime =>
      anime.id === animeId ? { ...anime, dropped: false, droppedAtEpisode: null } : anime
    );
    await saveData(newAnimes);
    // 詳細画面を開いている場合も即反映
    if (selectedAnime?.id === animeId) {
      setSelectedAnime({ ...selectedAnime, dropped: false, droppedAtEpisode: null });
    }
  };


  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4">
      <div className="max-w-6xl mx-auto">
        {/* ヘッダー */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mb-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="flex items-center gap-4">
              {selectedAnime && (
                <button
                  onClick={() => setSelectedAnime(null)}
                  className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded-lg transition-all"
                >
                  <Home size={20} />
                </button>
              )}
              <div>
                <div className="flex items-center gap-2">
                  <img src="/favicon.svg" alt="AniVal" className="w-8 h-8" />
                  <h1 className="text-3xl font-bold text-gray-800 dark:text-white">AniVal</h1>
                </div>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-sm text-gray-600 dark:text-gray-300">{user.email}</span>
                  {dataLoading ? (
                    <div className="flex items-center gap-1 text-orange-600">
                      <CloudOff size={14} className="animate-pulse" />
                      <span className="text-xs">{t('syncing')}</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 text-green-600">
                      <Cloud size={14} />
                      <span className="text-xs">
                        {lastSync ? t('syncedAt', { time: lastSync.toLocaleTimeString(language === 'ja' ? 'ja-JP' : 'en-US') }) : t('synced')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3 md:flex-row">
              {!selectedAnime && (
                <button
                  onClick={() => setShowAddForm(true)}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 py-3 rounded-lg flex items-center justify-center gap-2 transition-all duration-200 shadow-lg font-semibold whitespace-nowrap min-w-fit text-lg order-first"
                >
                  <Plus size={22} />
                  {t('addAnime')}
                </button>
              )}
              <div className="flex gap-3 flex-wrap">
                <button
                  onClick={toggleDarkMode}
                  className="bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 px-4 py-3 rounded-lg flex items-center gap-2 transition-all whitespace-nowrap"
                  title={isDarkMode ? 'ライトモードに切り替え' : 'ダークモードに切り替え'}
                >
                  {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
                </button>
                <button
                  onClick={toggleLanguage}
                  className="bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 px-4 py-3 rounded-lg flex items-center gap-2 transition-all whitespace-nowrap"
                  title={language === 'ja' ? 'Switch to English' : '日本語に切り替え'}
                >
                  <Globe size={20} />
                  {language === 'ja' ? 'EN' : 'JA'}
                </button>
                <button
                  onClick={signOut}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-3 rounded-lg flex items-center gap-2 transition-all whitespace-nowrap"
                >
                  <LogOut size={16} />
                  {t('logout')}
                </button>
              </div>
            </div>
          </div>

          {/* 検索・フィルター */}
          {!selectedAnime && (
            <SearchAndFilters {...filters} />
          )}
        </div>

        {/* 統計情報とグラフ */}
        {!selectedAnime && animes.length > 0 && (
          <>
            <StatsCard animes={filters.filteredAnimes} />
            <RatingTrendChart animes={animes} />
          </>
        )}

        {/* アニメ追加モーダル */}
        {showAddForm && (
          <AddAnimeModal
            onSave={addAnime}
            onCancel={() => setShowAddForm(false)}
            loading={dataLoading}
          />
        )}

        {/* アニメ編集モーダル */}
        {editingAnime && (
          <AnimeEditForm
            anime={editingAnime}
            onSave={updateAnime}
            onCancel={() => setEditingAnime(null)}
            uploadImage={uploadImage}
            suggestions={suggestions}
          />
        )}

        {/* エピソード評価モーダル */}
        {ratingEpisode && selectedAnime && (
          <EpisodeRatingModal
            episode={ratingEpisode}
            anime={selectedAnime}
            onSave={async (rating, notes, detailedRating, storyTitle) => {
              await updateEpisodeRating(
                selectedAnime.id,
                ratingEpisode.id,
                rating,
                notes,
                detailedRating,
                storyTitle
              );
              setRatingEpisode(null);
            }}
            onCancel={() => setRatingEpisode(null)}
            onDropAnime={async (episodeNumber) => {
              await dropAnime(selectedAnime.id, episodeNumber);
              setRatingEpisode(null);
            }}
            loading={dataLoading}
          />
        )}

        {/* アニメ一覧 or 詳細 */}
        {!selectedAnime ? (
          // アニメ一覧
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
            {filters.filteredAnimes.length === 0 ? (
              <div className="col-span-full text-center text-gray-400 py-12">
                データがありません
              </div>
            ) : (
              filters.filteredAnimes.map((anime) => (
                <AnimeCard
                  key={anime.id}
                  anime={anime}
                  onDelete={deleteAnime}
                  onClick={() => setSelectedAnime(anime)}
                  rank={getAnimeRank(anime, filters.allRatings)}
                  status={getAnimeStatus(anime)}
                />
              ))
            )}
          </div>
        ) : (
          // アニメ詳細
          <AnimeDetailView
            anime={selectedAnime}
            allRatings={filters.allRatings}
            onBack={() => setSelectedAnime(null)}
            onEdit={setEditingAnime}
            onRevive={reviveAnime}
            onRateEpisode={setRatingEpisode}
          />
        )}
      </div>
    </div>
  );
};

const App = () => {
  return (
    <DarkModeProvider>
      <LanguageProvider>
        <AnimeRatingApp />
      </LanguageProvider>
    </DarkModeProvider>
  );
};

export default App;
